import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import FilterList from '@mui/icons-material/FilterList';
import Refresh from '@mui/icons-material/Refresh';
import Close from '@mui/icons-material/Close';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import { GlobalSearchBar } from '../components/home/GlobalSearchBar';
import { KPIDashboard } from '../components/home/KPIDashboard';
import { FilterPane } from '../components/home/FilterPane';
import { EnhancedCallsDataTable } from '../components/home/EnhancedCallsDataTable';

import { CallService } from '../services/callService';
import { authService } from '../services/authService';
import { Call, CadInboxSummary, CallSearchRequest, PagedResult, KPIData, FilterValues } from '../types/Call';
import { useCallContext, MAX_CHAT_SCOPE_CALLS } from '../contexts/CallContext';
import { useRelativeTime } from '../hooks/useRelativeTime';
import { formatIsoDuration } from '../utils/timeUtils';
import { downloadFile } from '../utils/downloadFile';
import { isViteDev } from '../config';
import { visuallyHidden } from '@mui/utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createCSV(columns: { key: string; label: string }[], data: any[]): string {
  const headers = columns.map((col) => col.label).join(',');
  const rows = data.map((item) =>
    columns
      .map((col) => {
        let value = item[col.key] || '';
        if (col.key === 'fileDuration' && value && value.startsWith('PT')) {
          value = formatIsoDuration(value);
        }
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          value = `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      })
      .join(',')
  );
  return [headers, ...rows].join('\n');
}

// True when any classic (server-filterable) filter is set. Used both for the UI
// "filtered" indicators and to decide when a differential merge is safe.
function requestHasActiveFilters(r: Partial<CallSearchRequest>): boolean {
  return !!(
    r.searchTerm ||
    r.startDate ||
    r.endDate ||
    r.callType ||
    r.status ||
    r.reviewStatus ||
    r.operator ||
    r.cadReport ||
    r.minQualityScore ||
    r.maxQualityScore
  );
}

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [roleChecked, setRoleChecked] = useState(false);
  const [userRole, setUserRole] = useState<string>('');
  const [isPendingSetup, setIsPendingSetup] = useState(false);

  // PlatformAdmin should never see the calls dashboard — redirect to admin landing
  useEffect(() => {
    authService.getUserAsync().then(user => {
      if (user?.role === 'PlatformAdmin') {
        navigate('/admin', { replace: true });
      } else if (user?.isPendingSetup) {
        setIsPendingSetup(true);
        setRoleChecked(true);
      } else {
        setUserRole(user?.role || '');
        setRoleChecked(true);
      }
    });
  }, [navigate]);

  const [searchRequest, setSearchRequest] = useState<CallSearchRequest>({
    page: 1,
    pageSize: 25,
    sortBy: 'createdDate',
    sortDescending: true,
  });

  // Tracks what the user has typed but not yet submitted — decoupled from searchRequest.searchTerm
  // so typing alone does not trigger API calls or KPI recalculation.
  const [pendingSearchTerm, setPendingSearchTerm] = useState('');

  const [callsData, setCallsData] = useState<PagedResult<Call> | null>(null);
  const [kpiData, setKPIData] = useState<KPIData | null>(null);
  const [loading, setLoading] = useState(false);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [filterPaneOpen, setFilterPaneOpen] = useState(false);

  // Search pipeline mode. 'classic' = keyword/default server search; 'semantic' = smart
  // (text) search or similar-calls. searchMode drives which fetch path pagination, sort,
  // and filter changes take, so the two never silently overwrite each other.
  const [searchMode, setSearchMode] = useState<'classic' | 'semantic'>('classic');
  // The active smart-search query text (empty for the callId-based "similar calls" flow).
  const [semanticQuery, setSemanticQuery] = useState('');
  // True only when results are actually semantically ranked (drives the match-score column
  // and the KPI info chip). Distinct from searchMode because the backend may fall back to
  // keyword ranking while pagination still flows through the semantic endpoint.
  const [semanticMode, setSemanticMode] = useState(false);
  // Label for the persistent, dismissible smart-search chip.
  const [semanticBanner, setSemanticBanner] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const lastSyncTimestampRef = useRef<string | null>(null);
  const searchRequestRef = useRef<CallSearchRequest>(searchRequest);
  // Mirrors searchMode for use inside stable useCallbacks (loadCalls) without adding deps.
  const searchModeRef = useRef<'classic' | 'semantic'>('classic');
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  // Dynamic filter options from backend
  const [filterValues, setFilterValues] = useState<FilterValues | null>(null);
  // CAD reports waiting for a call. Null for an Agent and for an agency with CAD records off.
  const [cadInbox, setCadInbox] = useState<CadInboxSummary | null>(null);
  const [filterValuesLoading, setFilterValuesLoading] = useState(false);

  useEffect(() => {
    searchRequestRef.current = searchRequest;
  }, [searchRequest]);

  useEffect(() => {
    searchModeRef.current = searchMode;
  }, [searchMode]);

  const relativeTime = useRelativeTime(lastRefreshTime);

  const loadCalls = useCallback(
    async (forceFullRefresh = false, customSearchRequest?: CallSearchRequest) => {
      try {
        setLoading(true);
        setError(null);

        // Use the provided custom request or the current searchRequest from ref
        const requestToUse = customSearchRequest || searchRequestRef.current;
        if (isViteDev) console.log('Loading calls with request:', requestToUse);

        // Differential merge is only safe on the unfiltered, classic, page-1 default view.
        // With filters active (or in semantic mode) it would inject rows that don't match the
        // current view, so those paths always force a full server-filtered refresh.
        const useDifferential =
          !forceFullRefresh &&
          lastSyncTimestampRef.current &&
          !customSearchRequest &&
          requestToUse.page === 1 &&
          searchModeRef.current === 'classic' &&
          !requestHasActiveFilters(requestToUse);

        if (useDifferential) {
          if (isViteDev) console.log('Using differential update');
          const data = await CallService.searchCallsWithDiff(
            requestToUse,
            lastSyncTimestampRef.current || undefined
          );

          if (data.changesSince) {
            // Apply differential changes to existing data
            setCallsData((prevData) => {
              if (!prevData) return data;

              const updatedItems = [...prevData.items];
              const itemMap = new Map(updatedItems.map((item) => [item.callId, item]));

              // Apply deletions
              data.changesSince!.deleted.forEach((callId) => {
                itemMap.delete(callId);
              });

              // Apply additions and updates
              [...data.changesSince!.added, ...data.changesSince!.updated].forEach((call) => {
                itemMap.set(call.callId, call);
              });

              return {
                ...data,
                items: Array.from(itemMap.values()),
              };
            });
          } else {
            // No changes detected, but update the data anyway to ensure consistency
            setCallsData(data);
          }

          lastSyncTimestampRef.current = data.lastModified;
        } else {
          if (isViteDev) console.log('Using full refresh for page:', requestToUse.page);
          const data = await CallService.searchCallsWithDiff(requestToUse);

          setCallsData(data);
          lastSyncTimestampRef.current = data.lastModified;
        }

        setLastRefreshTime(new Date());
      } catch (_err) {
        setError('Failed to load calls. Please try again.');
        console.error('Error loading calls:', _err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const loadKPIData = useCallback(async (customSearchRequest?: CallSearchRequest) => {
    try {
      setKpiLoading(true);
      // Use the provided custom request or the current searchRequest from ref
      const request = customSearchRequest || searchRequestRef.current;
      const filters = {
        searchTerm: request.searchTerm,
        startDate: request.startDate,
        endDate: request.endDate,
        callType: request.callType,
        status: request.status,
        reviewStatus: request.reviewStatus,
        operator: request.operator,
        cadReport: request.cadReport,
        minQualityScore: request.minQualityScore,
        maxQualityScore: request.maxQualityScore,
      };
      const data = await CallService.getKPIData(filters);
      setKPIData(data);
      setLastRefreshTime(new Date());
    } catch (_err) {
      console.error('Error loading KPI data:', _err);
      setError('Failed to load KPI metrics. Please try again.');
    } finally {
      setKpiLoading(false);
    }
  }, []);

  // A promise chain, as the other loaders that set state from an effect-started call: the summary
  // is a convenience, and a failure to read it must not disturb the table.
  const loadCadInbox = useCallback(() => {
    CallService.getCadInboxSummary()
      .then(setCadInbox)
      .catch(() => setCadInbox(null));
  }, []);

  const loadFilterValues = useCallback(async () => {
    try {
      setFilterValuesLoading(true);
      const values = await CallService.getFilterValues();
      setFilterValues(values);
    } catch (_err) {
      // Non-critical — filter dropdowns will just be empty
      console.error('Error loading filter values:', _err);
    } finally {
      setFilterValuesLoading(false);
    }
  }, []);

  // Runs a smart (semantic) search for the current query, sending the full classic filter set
  // and the current page/pageSize/sort so the server paginates the ranked results. This is the
  // single fetch path for semantic mode — pagination, sort, and filter changes all re-invoke it.
  const runSemanticSearch = useCallback(async (query: string) => {
    try {
      setLoading(true);
      setError(null);
      const req = searchRequestRef.current;
      const result = await CallService.semanticSearch({
        query,
        page: req.page,
        pageSize: req.pageSize || 25,
        startDate: req.startDate,
        endDate: req.endDate,
        status: req.status,
        reviewStatus: req.reviewStatus,
        callType: req.callType,
        operator: req.operator,
        minQualityScore: req.minQualityScore,
        maxQualityScore: req.maxQualityScore,
      });
      const pageSize = result.pageSize || req.pageSize || 25;
      const totalPages = Math.max(1, Math.ceil(result.totalCount / pageSize));
      setCallsData({
        items: result.items,
        totalCount: result.totalCount,
        page: result.page,
        pageSize,
        totalPages,
        hasNextPage: result.page < totalPages,
        hasPreviousPage: result.page > 1,
      });
      // Only mark as semantically ranked when the vector search actually ran.
      setSemanticMode(result.searchedBy === 'semantic');
      setLastRefreshTime(new Date());
    } catch (_err) {
      setError('Smart search failed. Please try again.');
      console.error('Error running smart search:', _err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!roleChecked || isPendingSetup) return;

    const initializeData = async () => {
      if (isViteDev) {
        // Check if auth is cached (avoid repeated API calls)
        const authCached = sessionStorage.getItem('auth_verified');
        if (!authCached) {
          await authService.ensureAuthenticated();
          sessionStorage.setItem('auth_verified', 'true');
        }
      }
      loadCalls(true); // Force full refresh on initial load
      loadKPIData();
      loadFilterValues();
      loadCadInbox();
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleChecked]); // Run once auth completes

  // Single fetch trigger. Any change to the search request (pagination, sort, filters) OR the
  // search mode reloads the table through exactly one path, plus one KPI refresh. In semantic
  // mode pagination/sort/filter changes re-run the semantic search (server-paginated); KPIs stay
  // classic-filtered in both modes so they always reflect the sidebar filters, never the ranking.
  useEffect(() => {
    // Skip the initial load (handled by the initialization effect above)
    if (callsData === null) return;
    if (isViteDev) console.log('SearchRequest changed, reloading calls:', searchRequest, searchMode);
    if (searchMode === 'semantic') {
      // A query-less semantic view (callId-based "similar calls") has nothing to re-fetch —
      // leave its fixed result set intact rather than clobbering it with a classic load.
      if (semanticQuery) runSemanticSearch(semanticQuery);
    } else {
      loadCalls(true);
    }
    loadKPIData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchRequest.page,
    searchRequest.pageSize,
    searchRequest.sortBy,
    searchRequest.sortDescending,
    searchRequest.searchTerm,
    searchRequest.startDate,
    searchRequest.endDate,
    searchRequest.callType,
    searchRequest.status,
    searchRequest.reviewStatus,
    searchRequest.operator,
    searchRequest.cadReport,
    searchRequest.minQualityScore,
    searchRequest.maxQualityScore,
    searchMode,
    semanticQuery,
    loadCalls,
    loadKPIData,
    runSemanticSearch,
  ]);

  const handleSearchChange = (searchTerm: string) => {
    setPendingSearchTerm(searchTerm);

    // Backspacing a committed keyword search to empty resets it. State only — the watch
    // effect performs the single reload. (The X button routes through onClearAll instead.)
    if (!searchTerm && searchRequest.searchTerm) {
      if (isViteDev) console.log('Search cleared, resetting');
      setSearchRequest((prev) => ({ ...prev, searchTerm: '', page: 1 }));
      lastSyncTimestampRef.current = null;
    }
  };

  const handleSearch = () => {
    // A keyword search always drops out of semantic mode; the watch effect then runs
    // exactly one classic search for the committed term.
    if (searchMode === 'semantic') {
      setSearchMode('classic');
      setSemanticQuery('');
      setSemanticMode(false);
      setSemanticBanner(null);
      setAutoRefreshEnabled(true);
    }
    setSearchRequest((prev) => ({ ...prev, searchTerm: pendingSearchTerm, page: 1 }));
  };

  // Called once, when the FilterPane "Apply" button commits the local edits. Only sets state;
  // the watch effect is the single trigger that reloads the table and KPIs.
  const handleFiltersChange = (filters: Partial<CallSearchRequest>) => {
    setSearchRequest((prev) => ({
      ...prev,
      ...filters,
      page: 1,
    }));
  };

  const handleClearFilters = () => {
    const clearedRequest: CallSearchRequest = {
      page: 1,
      pageSize: 25,
      sortBy: 'createdDate',
      sortDescending: true,
    };
    setPendingSearchTerm('');
    setSearchRequest(clearedRequest);
    lastSyncTimestampRef.current = null;

    // A full reset also drops out of semantic mode. State only — the watch effect fires once
    // (searchMode and/or the request changed) and reloads the cleared, classic view.
    setSearchMode('classic');
    setSemanticQuery('');
    setSemanticMode(false);
    setSemanticBanner(null);
    setAutoRefreshEnabled(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleExport = async (exportData: { format: 'csv' | 'excel'; visibleColumns: { key: string; label: string }[]; currentData: any[] }) => {
    try {
      const blob = new Blob([createCSV(exportData.visibleColumns, exportData.currentData)], { type: 'text/csv;charset=utf-8;' });
      downloadFile(blob, 'calls-export.csv');
    } catch (_err) {
      setError('Failed to export data as CSV');
      console.error('Export error:', _err);
    }
  };

  // Reload the current view without changing mode — used by the manual Refresh button and
  // after a mass update. Semantic views re-run the query (never a classic load that would
  // clobber the ranked results); KPIs always refresh classic-filtered.
  const refreshCurrentView = useCallback(() => {
    if (searchMode === 'semantic') {
      if (semanticQuery) runSemanticSearch(semanticQuery);
    } else {
      loadCalls(true);
    }
    loadKPIData();
    loadCadInbox();
  }, [searchMode, semanticQuery, runSemanticSearch, loadCalls, loadKPIData, loadCadInbox]);

  const handleRefresh = () => refreshCurrentView();

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefreshEnabled) return;

    const interval = setInterval(async () => {
      try {
        // Only check for updates if we have data loaded and not currently loading
        if (callsData && !loading && !kpiLoading && lastSyncTimestampRef.current) {
          const hasUpdates = await CallService.checkForUpdates(lastSyncTimestampRef.current);

          if (hasUpdates) {
            if (isViteDev) console.log('Auto-refresh: Updates detected, refreshing data...');
            loadCalls(false); // Use differential refresh
            loadKPIData();
          }
        }
      } catch (_error) {
        console.error('Auto-refresh check failed:', _error);
        // Silently fail - don't disrupt user experience
      }
    }, 15000); // Check every 15 seconds

    return () => clearInterval(interval);
  }, [autoRefreshEnabled, callsData, loading, kpiLoading, loadCalls, loadKPIData]);

  const toggleAutoRefresh = () => {
    setAutoRefreshEnabled(!autoRefreshEnabled);
  };

  // Handle navigation state from "Find Similar Calls" button on call detail page
  useEffect(() => {
    const state = location.state as { similarToCallId?: string; similarToIncident?: string } | null;
    if (!state?.similarToCallId || !roleChecked) return;

    // Clear the navigation state so back-navigation doesn't re-trigger
    window.history.replaceState({}, '', window.location.pathname);

    const loadSimilar = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await CallService.findSimilarCalls(state.similarToCallId!);
        const pageSize = result.items.length || 25;
        const adaptedResult: PagedResult<Call> = {
          items: result.items,
          totalCount: result.items.length,
          page: 1,
          pageSize,
          totalPages: 1,
          hasNextPage: false,
          hasPreviousPage: false,
        };
        setCallsData(adaptedResult);
        // Enter semantic mode with no text query: this is a fixed, callId-based result set, so
        // the watch effect must not re-fetch it on sort changes. searchMode='semantic' keeps a
        // classic load from clobbering it.
        setSearchMode('semantic');
        setSemanticQuery('');
        setSemanticMode(true);
        setSemanticBanner(`Showing calls with similar patterns to ${state.similarToIncident || state.similarToCallId}`);
        setAutoRefreshEnabled(false);
      } catch (_err) {
        setError('Failed to load similar calls. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadSimilar();
  // roleChecked must be in deps so this runs after auth completes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleChecked]);

  const handleSmartSearch = (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) {
      // An empty query behaves like a keyword-search commit (and exits semantic mode).
      handleSearch();
      return;
    }
    // Enter semantic mode and record the query. The watch effect performs the single semantic
    // fetch (with the full classic filter set + page 1); subsequent pagination, sort, and filter
    // changes re-run it through that same path so the ranked view is never silently replaced.
    setSemanticQuery(trimmed);
    setSemanticBanner(`Smart search: "${trimmed}"`);
    setAutoRefreshEnabled(false);
    setSearchMode('semantic');
    setSearchRequest((prev) => ({ ...prev, page: 1 }));
  };

  const clearSemanticMode = () => {
    // Exit semantic mode but keep any active sidebar filters. The watch effect fires (searchMode
    // changed) and runs exactly one classic load for the current filters. Clear the search box too
    // so it doesn't keep showing the smart-search query over classic results.
    setSearchMode('classic');
    setSemanticQuery('');
    setSemanticMode(false);
    setSemanticBanner(null);
    setPendingSearchTerm('');
    setAutoRefreshEnabled(true);
    lastSyncTimestampRef.current = null;
    setSearchRequest((prev) => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = useMemo(
    () => requestHasActiveFilters(searchRequest),
    [searchRequest]
  );

  // ── Chatbot "Current results" scope ────────────────────────────────────────
  // Publish the currently viewed (filtered or smart-searched) result set so the
  // chatbot can answer questions scoped to it. The server re-validates every ID.
  const { setResultsScope, setChatScope, setIsChatbotOpen } = useCallContext();

  const resultsScopeActive =
    (hasActiveFilters || searchMode === 'semantic') && !!callsData && callsData.items.length > 0;

  useEffect(() => {
    if (resultsScopeActive && callsData) {
      setResultsScope({
        calls: callsData.items.slice(0, MAX_CHAT_SCOPE_CALLS).map((c) => ({
          callId: c.callId,
          incidentNumber: c.incidentNumber,
        })),
        totalCount: callsData.items.length,
      });
    } else {
      setResultsScope(undefined);
    }
  }, [resultsScopeActive, callsData, setResultsScope]);

  // Leaving the page invalidates the scope — the chatbot falls back to "All calls".
  useEffect(() => () => setResultsScope(undefined), [setResultsScope]);

  const handleAskAiAboutResults = () => {
    setChatScope('results');
    setIsChatbotOpen(true);
  };

  // Block rendering until role check completes — prevents 403 errors from tenant-scoped API calls
  if (!roleChecked) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // User authenticated but not yet assigned to a tenant — show actionable message instead of 403 errors
  if (isPendingSetup) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box sx={{ maxWidth: 480, textAlign: 'center', p: 4 }}>
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="subtitle2" gutterBottom>Account setup in progress</Typography>
            <Typography variant="body2">
              Your sign-in was successful, but your account has not been configured for access yet.
              Your administrator needs to complete your setup before you can view any data.
            </Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            If you believe this is an error, contact your GovIQ CallScore administrator at{' '}
            <strong>contact@goviq.us</strong> and provide your work email address.
          </Typography>
          <Button variant="outlined" size="small" onClick={() => window.location.reload()}>
            Refresh
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        CallScore Dashboard
      </Typography>

      <Stack spacing={3}>
        {/* Persistent, dismissible smart-search chip — dismissing exits semantic mode */}
        {semanticBanner && (
          <Box role="status" aria-live="polite">
            <Chip
              label={semanticBanner}
              color="primary"
              variant="outlined"
              onDelete={clearSemanticMode}
              deleteIcon={<Close fontSize="small" />}
              // Keep the visible label (which carries the query) in the accessible name so voice
              // control and screen readers get the query, then append the dismiss hint (WCAG 2.5.3).
              aria-label={`${semanticBanner}. Press Delete to dismiss.`}
              sx={{
                maxWidth: '100%',
                height: 'auto',
                alignItems: 'flex-start',
                '& .MuiChip-label': { whiteSpace: 'normal', py: 0.75 },
                '& .MuiChip-deleteIcon': { mt: 0.75 },
              }}
            />
          </Box>
        )}

        {/* Search and Filter Controls */}
        <Box display="flex" gap={2} alignItems="center">
          <GlobalSearchBar
            value={pendingSearchTerm}
            onChange={handleSearchChange}
            onSearch={handleSearch}
            onClearAll={handleClearFilters}
            onSmartSearch={handleSmartSearch}
          />
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setFilterPaneOpen(true)}
            color="inherit"
            sx={hasActiveFilters ? { color: 'primary.main', borderColor: 'primary.main' } : undefined}
          >
            Filters
          </Button>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={loading || kpiLoading}
            >
              Refresh
            </Button>
            <Tooltip title={autoRefreshEnabled ? 'Auto-refresh on — click to disable' : 'Auto-refresh off — click to enable'}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={toggleAutoRefresh}
                aria-label={`Auto-refresh ${autoRefreshEnabled ? 'on' : 'off'}`}
                sx={autoRefreshEnabled
                  ? { color: 'primary.main', borderColor: 'primary.main' }
                  : { color: 'text.disabled', borderColor: 'action.disabled' }
                }
              >
                Auto
              </Button>
            </Tooltip>
            {lastRefreshTime && (
              <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                last updated {relativeTime}
                {autoRefreshEnabled && ' • auto-refresh on'}
              </Typography>
            )}
          </Box>
        </Box>

        {/* KPI Dashboard — always reflects the classic sidebar filters, never the smart ranking */}
        <KPIDashboard
          data={kpiData}
          loading={kpiLoading}
          filtered={hasActiveFilters}
          semanticMode={semanticMode}
          onClearFilters={handleClearFilters}
        />

        {/* Ask AI about the filtered result set — opens the chatbot in "Current results" scope.
            The count reflects the server's 50-call analysis cap, never over-claims. */}
        {resultsScopeActive && callsData && (
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<AutoAwesome />}
              onClick={handleAskAiAboutResults}
            >
              {callsData.items.length > MAX_CHAT_SCOPE_CALLS
                ? `Ask AI about these results (first ${MAX_CHAT_SCOPE_CALLS} of ${callsData.items.length})`
                : `Ask AI about these results (${callsData.items.length})`}
            </Button>
          </Box>
        )}

        {/* The CAD filter is a quiet change to the table: say it is on, how many it found, and let it be
            removed from here as well as from the filter pane. */}
        {searchRequest.cadReport && (
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              label={`CAD report: ${searchRequest.cadReport}`}
              onDelete={() => setSearchRequest((prev) => ({ ...prev, cadReport: undefined, page: 1 }))}
              deleteIcon={<Close fontSize="small" />}
              aria-label={`CAD report: ${searchRequest.cadReport}. Press Delete to remove this filter.`}
              color="primary"
              variant="outlined"
            />
            <Box role="status" aria-live="polite" sx={visuallyHidden}>
              {callsData && !loading
                ? `Showing ${callsData.totalCount} ${callsData.totalCount === 1 ? 'call' : 'calls'} ${
                    searchRequest.cadReport === 'Missing' ? 'missing a CAD report' : 'with a CAD report'
                  }.`
                : ''}
            </Box>
          </Box>
        )}

        {/* CAD reports no call has claimed yet. One line, one action: the dashboard filter that lists
            the calls still missing a report, where each can be opened and given one of these. */}
        {cadInbox && cadInbox.waiting > 0 && (userRole === 'Supervisor' || userRole === 'TenantAdmin') && (
          <Alert
            severity="info"
            role="status"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setSearchRequest((prev) => ({ ...prev, cadReport: 'Missing', page: 1 }))}
              >
                Show calls missing a CAD report
              </Button>
            }
          >
            {cadInbox.waiting === 1
              ? '1 CAD report is waiting for a call.'
              : `${cadInbox.waiting} CAD reports are waiting for a call.`}
          </Alert>
        )}

        {/* Calls Data Table */}
        <EnhancedCallsDataTable
          data={callsData}
          loading={loading}
          searchRequest={searchRequest}
          onSearchRequestChange={setSearchRequest}
          onExport={handleExport}
          userRole={userRole}
          onMassUpdateComplete={refreshCurrentView}
          semanticMode={semanticMode}
        />
      </Stack>

      {/* Filter Pane */}
      <FilterPane
        open={filterPaneOpen}
        onClose={() => setFilterPaneOpen(false)}
        filters={searchRequest}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
        filterOptions={filterValues ?? undefined}
        optionsLoading={filterValuesLoading}
        cadEnabled={!!cadInbox || !!searchRequest.cadReport || !!callsData?.items.some((call) => call.cadStatus)}
      />

      {/* Error Snackbar */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};
